import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import {
  Ban,
  Building2,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Clock3,
  Download,
  Eye,
  FileCheck2,
  FilePlus2,
  FileText,
  History,
  Loader2,
  Mail,
  MapPin,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Settings2,
  ShieldCheck,
  UserCheck,
  X,
  XCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import SkeletonBlock from '../../components/common/SkeletonBlock';

import { adminAPI } from '../../services/api';
import useAuthStore from '../../store/authStore';
import { hasPermission } from '../../utils/rbac';

const queueStatuses = [
  { value: 'open', label: 'Open queue' },
  { value: 'pending', label: 'Pending' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'under_review', label: 'Under review' },
  { value: 'completed', label: 'Completed' },
  { value: 'all', label: 'All records' }
];

const subjectOptions = [
  { value: 'company', label: 'Company', help: 'One document for the company.' },
  { value: 'director', label: 'Directors', help: 'Repeat this document for each director.' },
  { value: 'beneficial_owner', label: 'Beneficial owners', help: 'Repeat this document for each beneficial owner.' },
  { value: 'authorized_representative', label: 'Authorized representatives', help: 'Repeat this document for people allowed to act for the company.' },
  { value: 'contact_person', label: 'Contact person', help: 'Use this when the document belongs to the main company contact.' },
  { value: 'other_person', label: 'Other person', help: 'Use this only when the person type does not fit the options above.' }
];

const categoryOptions = [
  { value: 'company', label: 'Company registration' },
  { value: 'identity', label: 'Identity' },
  { value: 'address', label: 'Address' },
  { value: 'tax', label: 'Tax' },
  { value: 'licence', label: 'Licence' },
  { value: 'financial', label: 'Financial' },
  { value: 'ownership', label: 'Ownership' },
  { value: 'other', label: 'Other' }
];

const validationOptions = [
  { value: 'none', label: 'No document number check', help: 'Accept any optional reference number. File review remains manual.' },
  { value: 'free_text', label: 'Any text', help: 'Allow letters, numbers, spaces, and punctuation.' },
  { value: 'numeric', label: 'Numbers only', help: 'Use for IDs or registration numbers that must only contain digits.' },
  { value: 'alphanumeric', label: 'Letters and numbers', help: 'Use for references that can contain letters, numbers, spaces, or hyphens.' },
  { value: 'regex', label: 'Custom format', help: 'Advanced document-number format. This does not validate the uploaded file contents.' }
];

const labelize = (value = '') => String(value || 'Not available')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (character) => character.toUpperCase())
  .replace(/\bKyc\b/g, 'KYC');

const slugifyDocumentType = (value = '') => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/&/g, ' and ')
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '')
  .replace(/_{2,}/g, '_');

const displayRequirementName = (requirement = {}) => requirement.displayName || labelize(requirement.documentType);

const subjectLabel = (subjectType) => subjectOptions.find((option) => option.value === subjectType)?.label || labelize(subjectType);

const formatRequirementAudience = (requirement = {}) => {
  const subject = subjectLabel(requirement.subjectType || 'company');
  if (!requirement.allowMultiple || requirement.subjectType === 'company') return subject;
  return `${subject} · ${requirement.minimumInstances || 1}-${requirement.maximumInstances || 1} people`;
};

const formatSides = (sides = ['single']) => {
  const normalized = Array.isArray(sides) && sides.length ? sides : ['single'];
  if (normalized.includes('single')) return 'One file';
  return normalized.map(labelize).join(' + ');
};

const formatDateTime = (value) => {
  if (!value) return 'Not available';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not available';
  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
};

const initialsFor = (name) => String(name || 'Employer').split(' ').filter(Boolean).map((part) => part[0]).join('').slice(0, 2).toUpperCase();

const formatBytes = (bytes) => {
  const size = Number(bytes) || 0;
  if (!size) return 'Size unavailable';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(size) / Math.log(1024)), units.length - 1);
  return `${(size / (1024 ** index)).toFixed(index ? 1 : 0)} ${units[index]}`;
};

const statusClass = (status) => ({
  verified: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  active: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  pending: 'border-sky-200 bg-sky-50 text-sky-700',
  assigned: 'border-violet-200 bg-violet-50 text-violet-700',
  under_review: 'border-amber-200 bg-amber-50 text-amber-700',
  in_progress: 'border-amber-200 bg-amber-50 text-amber-700',
  in_review: 'border-amber-200 bg-amber-50 text-amber-700',
  rejected: 'border-rose-200 bg-rose-50 text-rose-700',
  suspended: 'border-rose-200 bg-rose-50 text-rose-700',
  missing: 'border-slate-100 bg-slate-100 text-slate-600',
  completed: 'border-slate-100 bg-slate-100 text-slate-700'
}[status] || 'border-slate-100 bg-slate-100 text-slate-700');

const Badge = ({ status, children }) => (
  <span className={`inline-flex items-center rounded-xl border px-2 py-1 text-xs font-semibold ${statusClass(status)}`}>
    {children || labelize(status)}
  </span>
);

const SummaryCard = ({ icon: Icon, label, value, tone = 'cyan' }) => {
  const toneClass = {
    cyan: 'bg-blue-50 text-blue-700 ring-blue-100',
    amber: 'bg-amber-50 text-amber-700 ring-amber-100',
    violet: 'bg-violet-50 text-violet-700 ring-violet-100',
    emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-100'
  }[tone];
  return (
    <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</p>
          <p className="mt-2 text-2xl font-semibold text-slate-950">{Number(value) || 0}</p>
        </div>
        <span className={`flex h-10 w-10 items-center justify-center rounded-xl border border-slate-100/50 ${toneClass}`}>
          <Icon size={19} />
        </span>
      </div>
    </div>
  );
};

const EmptyState = ({ children }) => (
  <div className="rounded-[24px] border border-dashed border-slate-200 bg-slate-50/50 flex flex-col items-center justify-center px-5 py-8 text-center text-sm leading-6 text-slate-500">
    {children}
  </div>
);

const SummaryCardsSkeleton = () => (
  <section className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4" aria-label="Loading KYC summary">
    {Array.from({ length: 4 }).map((_, index) => (
      <SkeletonBlock key={index} className="h-28 rounded-xl" />
    ))}
  </section>
);

const QueueListSkeleton = () => (
  <div className="divide-y divide-slate-100" aria-label="Loading verification queue">
    {Array.from({ length: 7 }).map((_, index) => (
      <div key={index} className="grid gap-4 px-4 py-4 md:grid-cols-[minmax(220px,1.5fr)_120px_130px_130px_140px_90px] md:items-center">
        <div className="space-y-2">
          <SkeletonBlock className="h-4 w-44 rounded-xl" />
          <SkeletonBlock className="h-3 w-64 max-w-full rounded-xl" />
        </div>
        <SkeletonBlock className="h-7 w-20 rounded-xl" />
        <SkeletonBlock className="h-4 w-24 rounded-xl" />
        <SkeletonBlock className="h-4 w-24 rounded-xl" />
        <SkeletonBlock className="h-4 w-28 rounded-xl" />
        <SkeletonBlock className="h-9 w-20 rounded-xl" />
      </div>
    ))}
  </div>
);

const EmployerDetailSkeleton = () => (
  <div className="space-y-5" aria-label="Loading employer verification details">
    <section className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <SkeletonBlock className="h-7 w-56 rounded-xl" />
            <SkeletonBlock className="h-7 w-20 rounded-xl" />
            <SkeletonBlock className="h-7 w-20 rounded-xl" />
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {Array.from({ length: 5 }).map((_, index) => (
              <SkeletonBlock key={index} className="h-4 w-full rounded-xl" />
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <SkeletonBlock className="h-10 w-28 rounded-xl" />
          <SkeletonBlock className="h-10 w-28 rounded-xl" />
        </div>
      </div>
      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <SkeletonBlock key={index} className="h-20 rounded-xl" />
        ))}
      </div>
      <SkeletonBlock className="mt-5 h-24 rounded-xl" />
    </section>

    <section className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-start gap-3">
        <SkeletonBlock className="h-9 w-9 rounded-xl" />
        <div className="space-y-2">
          <SkeletonBlock className="h-5 w-44 rounded-xl" />
          <SkeletonBlock className="h-3 w-72 max-w-full rounded-xl" />
        </div>
      </div>
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <SkeletonBlock key={index} className="h-28 rounded-xl" />
        ))}
      </div>
    </section>

    <section className="grid gap-5 lg:grid-cols-2">
      <SkeletonBlock className="h-64 rounded-xl" />
      <SkeletonBlock className="h-64 rounded-xl" />
    </section>
  </div>
);

const RegionalPolicySkeleton = () => (
  <div className="grid gap-5 xl:grid-cols-[280px_minmax(0,1fr)]" aria-label="Loading regional KYC policies">
    <section className="overflow-hidden rounded-[24px] border border-slate-100 bg-white shadow-sm">
      <div className="border-b border-slate-100 px-4 py-3">
        <SkeletonBlock className="h-4 w-36 rounded-xl" />
        <SkeletonBlock className="mt-2 h-3 w-24 rounded-xl" />
      </div>
      {Array.from({ length: 5 }).map((_, index) => (
        <div key={index} className="border-b border-slate-100 px-4 py-4">
          <div className="flex items-center justify-between gap-3">
            <SkeletonBlock className="h-4 w-32 rounded-xl" />
            <SkeletonBlock className="h-7 w-16 rounded-xl" />
          </div>
          <SkeletonBlock className="mt-3 h-3 w-28 rounded-xl" />
          <SkeletonBlock className="mt-2 h-3 w-36 rounded-xl" />
        </div>
      ))}
    </section>
    <div className="min-w-0 space-y-5">
      <SkeletonBlock className="h-40 rounded-xl" />
      <SkeletonBlock className="h-96 rounded-xl" />
      <SkeletonBlock className="h-56 rounded-xl" />
    </div>
  </div>
);

const PolicyHistorySkeleton = () => (
  <div className="space-y-3" aria-label="Loading regional policy history">
    {Array.from({ length: 4 }).map((_, index) => (
      <div key={index} className="border-l-2 border-blue-100 pl-3">
        <SkeletonBlock className="h-4 w-36 rounded-xl" />
        <SkeletonBlock className="mt-2 h-3 w-full max-w-md rounded-xl" />
        <SkeletonBlock className="mt-2 h-3 w-44 rounded-xl" />
      </div>
    ))}
  </div>
);

const openBlob = (blob, filename, preview = false) => {
  const url = URL.createObjectURL(blob);
  if (preview) {
    window.open(url, '_blank', 'noopener,noreferrer');
    window.setTimeout(() => URL.revokeObjectURL(url), 30000);
    return;
  }
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || 'kyc-document';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const ReasonModal = ({ modal, reason, notes, loading, onReasonChange, onNotesChange, onClose, onConfirm }) => {
  if (!modal) return null;
  const config = {
    rejectDocument: {
      title: 'Reject verification document',
      description: `Explain why ${labelize(modal.document?.documentType)} cannot be accepted. The employer will receive this reason by email.`,
      confirm: 'Reject document',
      tone: 'bg-rose-600 hover:bg-rose-700'
    },
    rejectEmployer: {
      title: 'Reject employer verification',
      description: 'Provide the employer with a precise reason and the corrective action required.',
      confirm: 'Reject verification',
      tone: 'bg-rose-600 hover:bg-rose-700'
    },
    suspendEmployer: {
      title: 'Suspend employer account',
      description: 'This blocks the organization workspace, revokes refresh tokens, and sends the reason to the employer.',
      confirm: 'Suspend account',
      tone: 'bg-rose-600 hover:bg-rose-700'
    }
  }[modal.type];
  if (!config) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm">
      <section className="w-full max-w-lg rounded-[24px] border border-slate-100 bg-white p-5 shadow-2xl sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-950">{config.title}</h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">{config.description}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <X size={18} />
          </button>
        </div>
        <label className="mt-5 block text-sm font-semibold text-slate-700">
          Reason
          <textarea
            value={reason}
            onChange={(event) => onReasonChange(event.target.value)}
            rows={4}
            maxLength={1000}
            className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm font-normal text-slate-900 outline-none ring-blue-500 focus:ring-2"
            placeholder="Enter a specific reason with the corrective action required."
          />
        </label>
        {modal.type !== 'suspendEmployer' && (
          <label className="mt-4 block text-sm font-semibold text-slate-700">
            Internal note <span className="font-normal text-slate-400">(optional)</span>
            <textarea
              value={notes}
              onChange={(event) => onNotesChange(event.target.value)}
              rows={2}
              maxLength={2000}
              className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm font-normal text-slate-900 outline-none ring-blue-500 focus:ring-2"
              placeholder="Add context for the audit trail."
            />
          </label>
        )}
        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button type="button" onClick={onClose} className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading || reason.trim().length < 10}
            className={`rounded-xl px-4 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50 ${config.tone}`}
          >
            {loading ? 'Saving...' : config.confirm}
          </button>
        </div>
      </section>
    </div>
  );
};

const RequirementModal = ({ modal, loading, onClose, onConfirm }) => {
  const [form, setForm] = useState({
    documentType: '',
    displayName: '',
    documentDescription: '',
    isRequired: true,
    isActive: true,
    documentCategory: 'company',
    subjectType: 'company',
    allowMultiple: false,
    minimumInstances: 1,
    maximumInstances: 1,
    requiredSides: ['single'],
    instanceLabel: '',
    identifierLabel: '',
    validationRule: 'none',
    validationPattern: '',
    validationHelpText: '',
    uploadInstructions: '',
    changeSummary: ''
  });

  useEffect(() => {
    const requirement = modal?.requirement;
    const nextSubjectType = requirement?.subjectType || 'company';
    setForm({
      documentType: requirement?.documentType || '',
      displayName: requirement?.displayName || '',
      documentDescription: requirement?.description || '',
      isRequired: requirement?.isRequired ?? true,
      isActive: requirement?.isActive ?? true,
      documentCategory: requirement?.documentCategory || 'company',
      subjectType: nextSubjectType,
      allowMultiple: requirement?.allowMultiple ?? false,
      minimumInstances: requirement?.minimumInstances || 1,
      maximumInstances: requirement?.maximumInstances || 1,
      requiredSides: requirement?.requiredSides?.length ? requirement.requiredSides : ['single'],
      instanceLabel: requirement?.instanceLabel || (nextSubjectType === 'company' ? 'Company' : labelize(nextSubjectType).replace(/s$/, '')),
      identifierLabel: requirement?.identifierLabel || '',
      validationRule: requirement?.validationRule || (requirement?.validationPattern ? 'regex' : 'none'),
      validationPattern: requirement?.validationPattern || '',
      validationHelpText: requirement?.validationHelpText || '',
      uploadInstructions: requirement?.uploadInstructions || '',
      changeSummary: ''
    });
  }, [modal]);

  if (!modal) return null;
  const editing = modal.type === 'editRequirement';
  const usesMultiplePeople = form.subjectType !== 'company';
  const maxInstances = Math.max(Number(form.maximumInstances) || 1, Number(form.minimumInstances) || 1);
  const valid = form.displayName.trim().length >= 2 &&
    form.documentDescription.trim().length >= 10 &&
    form.changeSummary.trim().length >= 10 &&
    form.requiredSides.length > 0 &&
    (form.validationRule !== 'regex' || form.validationPattern.trim().length >= 2) &&
    (editing || /^[a-z0-9]+(?:_[a-z0-9]+)*$/.test((form.documentType || slugifyDocumentType(form.displayName)).trim()));

  const setSide = (side, checked) => {
    setForm((current) => {
      if (side === 'single') return { ...current, requiredSides: ['single'] };
      const sides = new Set(current.requiredSides.filter((item) => item !== 'single'));
      if (checked) sides.add(side);
      else sides.delete(side);
      const nextSides = ['front', 'back'].filter((item) => sides.has(item));
      return { ...current, requiredSides: nextSides.length ? nextSides : ['single'] };
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm">
      <form
        className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-[24px] border border-slate-100 bg-white p-5 shadow-2xl sm:p-6"
        onSubmit={(event) => {
          event.preventDefault();
          if (valid) onConfirm({
            ...form,
            maximumInstances: maxInstances,
            documentType: form.documentType || slugifyDocumentType(form.displayName)
          });
        }}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-950">{editing ? 'Edit regional requirement' : 'Add regional requirement'}</h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Create the document request in plain business terms. The employer upload page uses these settings to show the right fields.
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <X size={18} />
          </button>
        </div>

        <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px]">
          <label className="block text-sm font-semibold text-slate-700">
            Document name
            <input
              value={form.displayName}
              onChange={(event) => setForm((current) => ({
                ...current,
                displayName: event.target.value,
                documentType: editing ? current.documentType : slugifyDocumentType(event.target.value)
              }))}
              maxLength={160}
              placeholder="Certificate of incorporation"
              className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm font-normal text-slate-900 outline-none ring-blue-500 focus:ring-2"
            />
            <span className="mt-1 block text-xs font-normal text-slate-500">This is the name employers will see.</span>
          </label>
          <label className="block text-sm font-semibold text-slate-700">
            Category
            <select
              value={form.documentCategory}
              onChange={(event) => setForm((current) => ({ ...current, documentCategory: event.target.value }))}
              className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm font-normal text-slate-900 outline-none ring-blue-500 focus:ring-2"
            >
              {categoryOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </label>
        </div>

        <label className="mt-4 block text-sm font-semibold text-slate-700">
          What should the employer upload?
          <textarea
            value={form.documentDescription}
            onChange={(event) => setForm((current) => ({ ...current, documentDescription: event.target.value }))}
            rows={3}
            maxLength={2000}
            placeholder="Example: Upload the official certificate issued by the company registry. The company name must match the employer profile."
            className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm font-normal text-slate-900 outline-none ring-blue-500 focus:ring-2"
          />
        </label>

        <div className="mt-4 rounded-2xl border border-slate-100 bg-slate-50 p-4">
          <p className="text-sm font-semibold text-slate-900">Who does this document belong to?</p>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <label className="block text-sm font-semibold text-slate-700">
              Person or company
              <select
                value={form.subjectType}
                onChange={(event) => {
                  const nextSubject = event.target.value;
                  setForm((current) => ({
                    ...current,
                    subjectType: nextSubject,
                    allowMultiple: nextSubject === 'company' ? false : current.allowMultiple,
                    minimumInstances: nextSubject === 'company' ? 1 : current.minimumInstances,
                    maximumInstances: nextSubject === 'company' ? 1 : Math.max(current.maximumInstances, current.minimumInstances),
                    instanceLabel: nextSubject === 'company' ? 'Company' : labelize(nextSubject).replace(/s$/, '')
                  }));
                }}
                className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm font-normal text-slate-900 outline-none ring-blue-500 focus:ring-2"
              >
                {subjectOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
              <span className="mt-1 block text-xs font-normal text-slate-500">
                {subjectOptions.find((option) => option.value === form.subjectType)?.help}
              </span>
            </label>
            <label className="block text-sm font-semibold text-slate-700">
              Label for each person
              <input
                value={form.instanceLabel}
                onChange={(event) => setForm((current) => ({ ...current, instanceLabel: event.target.value }))}
                disabled={form.subjectType === 'company'}
                maxLength={100}
                placeholder="Director"
                className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm font-normal text-slate-900 outline-none ring-blue-500 focus:ring-2 disabled:bg-slate-100 disabled:text-slate-500"
              />
            </label>
          </div>
          {usesMultiplePeople && (
            <div className="mt-3 grid gap-3 md:grid-cols-[1fr_120px_120px] md:items-end">
              <label className="flex items-start gap-3 rounded-xl border border-white bg-white p-3 text-sm text-slate-700 shadow-sm">
                <input
                  type="checkbox"
                  checked={form.allowMultiple}
                  onChange={(event) => setForm((current) => ({ ...current, allowMultiple: event.target.checked }))}
                  className="mt-0.5 h-4 w-4 accent-blue-700"
                />
                <span><strong className="block text-slate-900">Can repeat for several people</strong>Use this for director IDs, owner IDs, or similar documents.</span>
              </label>
              <label className="block text-sm font-semibold text-slate-700">
                Minimum
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={form.minimumInstances}
                  disabled={!form.allowMultiple}
                  onChange={(event) => setForm((current) => ({ ...current, minimumInstances: Number(event.target.value) || 1 }))}
                  className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm font-normal text-slate-900 outline-none ring-blue-500 focus:ring-2 disabled:bg-slate-100 disabled:text-slate-500"
                />
              </label>
              <label className="block text-sm font-semibold text-slate-700">
                Maximum
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={maxInstances}
                  disabled={!form.allowMultiple}
                  onChange={(event) => setForm((current) => ({ ...current, maximumInstances: Number(event.target.value) || 1 }))}
                  className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm font-normal text-slate-900 outline-none ring-blue-500 focus:ring-2 disabled:bg-slate-100 disabled:text-slate-500"
                />
              </label>
            </div>
          )}
        </div>

        <div className="mt-4 rounded-2xl border border-slate-100 bg-white p-4">
          <p className="text-sm font-semibold text-slate-900">How many files are needed?</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            {[
              { value: 'single', label: 'One file', help: 'Use for certificates, licences, tax PINs, and one-page documents.' },
              { value: 'front', label: 'Front side', help: 'Use when the ID card has separate front and back images.' },
              { value: 'back', label: 'Back side', help: 'Require this together with front side.' }
            ].map((option) => (
              <label key={option.value} className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 text-sm transition ${form.requiredSides.includes(option.value) ? 'border-blue-200 bg-blue-50 text-blue-900' : 'border-slate-100 bg-slate-50 text-slate-700 hover:bg-slate-100'}`}>
                <input
                  type="checkbox"
                  checked={form.requiredSides.includes(option.value)}
                  onChange={(event) => setSide(option.value, event.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-blue-700"
                />
                <span><strong className="block">{option.label}</strong><span className="text-xs font-normal leading-5">{option.help}</span></span>
              </label>
            ))}
          </div>
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <label className="block text-sm font-semibold text-slate-700">
            Document number check
            <select
              value={form.validationRule}
              onChange={(event) => setForm((current) => ({
                ...current,
                validationRule: event.target.value,
                validationPattern: event.target.value === 'regex' ? current.validationPattern : ''
              }))}
              className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm font-normal text-slate-900 outline-none ring-blue-500 focus:ring-2"
            >
              {validationOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
            <span className="mt-1 block text-xs font-normal text-slate-500">
              {validationOptions.find((option) => option.value === form.validationRule)?.help}
            </span>
          </label>
          <label className="block text-sm font-semibold text-slate-700">
            Document number label
            <input
              value={form.identifierLabel}
              onChange={(event) => setForm((current) => ({ ...current, identifierLabel: event.target.value }))}
              maxLength={120}
              placeholder="Registration number"
              className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm font-normal text-slate-900 outline-none ring-blue-500 focus:ring-2"
            />
          </label>
        </div>

        {form.validationRule === 'regex' && (
          <label className="mt-4 block text-sm font-semibold text-slate-700">
            Custom document number format
            <input
              value={form.validationPattern}
              onChange={(event) => setForm((current) => ({ ...current, validationPattern: event.target.value }))}
              maxLength={255}
              placeholder="Example: ^[A-Z0-9-]{6,20}$"
              className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 font-mono text-sm font-normal text-slate-900 outline-none ring-blue-500 focus:ring-2"
            />
            <span className="mt-1 block text-xs font-normal text-slate-500">
              Advanced use only. This checks the typed reference number, not whether the uploaded document is genuine.
            </span>
          </label>
        )}

        <label className="mt-4 block text-sm font-semibold text-slate-700">
          Extra upload instructions <span className="font-normal text-slate-400">(optional)</span>
          <textarea
            value={form.uploadInstructions}
            onChange={(event) => setForm((current) => ({ ...current, uploadInstructions: event.target.value }))}
            rows={2}
            maxLength={1000}
            placeholder="Example: Upload a clear PDF or image. Names and document numbers must be readable."
            className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm font-normal text-slate-900 outline-none ring-blue-500 focus:ring-2"
          />
        </label>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <label className="flex items-start gap-3 rounded-xl border border-slate-100 bg-slate-50 p-3 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={form.isRequired}
              onChange={(event) => setForm((current) => ({ ...current, isRequired: event.target.checked }))}
              className="mt-0.5 h-4 w-4 accent-blue-700"
            />
            <span><strong className="block text-slate-900">Required</strong>Employers must supply this document.</span>
          </label>
          <label className="flex items-start gap-3 rounded-xl border border-slate-100 bg-slate-50 p-3 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(event) => setForm((current) => ({ ...current, isActive: event.target.checked }))}
              className="mt-0.5 h-4 w-4 accent-blue-700"
            />
            <span><strong className="block text-slate-900">Active</strong>Show this document in the regional policy.</span>
          </label>
        </div>
        <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50 p-3 text-sm leading-6 text-blue-900">
          Employers will see: <strong>{form.displayName || 'Document name'}</strong>
          {' '}for <strong>{subjectLabel(form.subjectType).toLowerCase()}</strong>
          {form.allowMultiple && form.subjectType !== 'company' ? ` (${form.minimumInstances}-${maxInstances} people)` : ''}
          {' '}· <strong>{formatSides(form.requiredSides).toLowerCase()}</strong>.
        </div>
        <label className="mt-4 block text-sm font-semibold text-slate-700">
          Change summary
          <textarea
            value={form.changeSummary}
            onChange={(event) => setForm((current) => ({ ...current, changeSummary: event.target.value }))}
            rows={2}
            maxLength={1000}
            placeholder="Explain why this regional policy is being changed."
            className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm font-normal text-slate-900 outline-none ring-blue-500 focus:ring-2"
          />
        </label>
        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button type="button" onClick={onClose} className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            Cancel
          </button>
          <button type="submit" disabled={!valid || loading} className="rounded-xl bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50">
            {loading ? 'Saving...' : editing ? 'Save requirement' : 'Add requirement'}
          </button>
        </div>
      </form>
    </div>
  );
};

const RegionalPolicyWorkspace = ({
  policies,
  loading,
  error,
  canManage,
  selectedRegionId,
  onSelectRegion,
  history,
  historyLoading,
  onAdd,
  onEdit
}) => {
  const selectedPolicy = policies.find((policy) => String(policy.id) === String(selectedRegionId)) || policies[0];

  if (loading) return <RegionalPolicySkeleton />;
  if (error) return <div className="rounded-[24px] border border-slate-100 bg-white p-6"><EmptyState>{error.message || 'Regional policies could not be loaded.'}</EmptyState></div>;
  if (!selectedPolicy) return <div className="rounded-[24px] border border-slate-100 bg-white p-6"><EmptyState>No KYC regions are configured.</EmptyState></div>;

  return (
    <div className="grid gap-5 xl:grid-cols-[280px_minmax(0,1fr)]">
      <section className="overflow-hidden rounded-[24px] border border-slate-100 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-slate-950">Regional policies</h2>
          <p className="mt-1 text-xs text-slate-500">{policies.length} configured regions</p>
        </div>
        <div>
          {policies.map((policy) => {
            const selected = String(policy.id) === String(selectedPolicy.id);
            return (
              <button key={policy.id} type="button" onClick={() => onSelectRegion(policy.id)} className={`block w-full border-b border-slate-100 px-4 py-4 text-left ${selected ? 'bg-blue-50' : 'hover:bg-slate-50'}`}>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-slate-950">{policy.regionName}</p>
                  <Badge status={policy.isActive ? 'active' : 'suspended'}>{policy.isActive ? 'Active' : 'Inactive'}</Badge>
                </div>
                <p className="mt-2 text-xs text-slate-500">{policy.regionCode} · Version {policy.policyVersion}</p>
                <p className="mt-1 text-xs text-slate-500">{policy.summary.required} required · {policy.summary.optional} optional</p>
              </button>
            );
          })}
        </div>
      </section>

      <div className="min-w-0 space-y-5">
        <section className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-xl font-semibold text-slate-950">{selectedPolicy.regionName}</h2>
                <Badge status="active">Policy version {selectedPolicy.policyVersion}</Badge>
              </div>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Requirement edits affect future submissions. {selectedPolicy.activeReviewSnapshots} active review snapshot{selectedPolicy.activeReviewSnapshots === 1 ? '' : 's'} remain locked to an earlier policy where applicable.
              </p>
            </div>
            {canManage && (
              <button type="button" onClick={() => onAdd(selectedPolicy)} className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-800">
                <Plus size={16} /> Add requirement
              </button>
            )}
          </div>
          {!canManage && (
            <p className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              You can inspect policy definitions. Editing requires the `config.manage` permission.
            </p>
          )}
        </section>

        <section className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-start gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 border border-blue-100/50"><FilePlus2 size={18} /></span>
            <div>
              <h2 className="text-base font-semibold text-slate-950">Document requirements</h2>
              <p className="mt-1 text-sm text-slate-500">Configure the documents employers can supply in this region.</p>
            </div>
          </div>
          <div className="space-y-3">
            {selectedPolicy.requirements.map((requirement) => (
              <article key={requirement.id} className={`rounded-xl border p-4 ${requirement.isActive ? 'border-slate-100' : 'border-slate-100 bg-slate-50 opacity-75'}`}>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-sm font-semibold text-slate-950">{displayRequirementName(requirement)}</h3>
                      <Badge status={requirement.isRequired ? 'under_review' : 'completed'}>{requirement.isRequired ? 'Required' : 'Optional'}</Badge>
                      {!requirement.isActive && <Badge status="suspended">Inactive</Badge>}
                    </div>
                    <p className="mt-2 text-sm leading-6 text-slate-600">{requirement.description}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <span className="rounded-xl bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">{formatRequirementAudience(requirement)}</span>
                      <span className="rounded-xl bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">{formatSides(requirement.requiredSides)}</span>
                      <span className="rounded-xl bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">{labelize(requirement.documentCategory || 'company')}</span>
                      <span className="rounded-xl bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                        {validationOptions.find((option) => option.value === requirement.validationRule)?.label || 'No document number check'}
                      </span>
                    </div>
                    <p className="mt-2 text-xs text-slate-400">Code: {requirement.documentType}</p>
                  </div>
                  {canManage && (
                    <button type="button" onClick={() => onEdit(selectedPolicy, requirement)} className="inline-flex shrink-0 items-center gap-2 rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                      <Pencil size={14} /> Edit
                    </button>
                  )}
                </div>
              </article>
            ))}
            {!selectedPolicy.requirements.length && <EmptyState>No document requirements have been defined for this region.</EmptyState>}
          </div>
        </section>

        <section className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center gap-3">
            <History size={18} className="text-blue-700" />
            <h2 className="text-base font-semibold text-slate-950">Policy version history</h2>
          </div>
          {historyLoading ? <PolicyHistorySkeleton /> : (
            <div className="space-y-3">
              {(history || []).map((entry) => (
                <div key={entry.id} className="border-l-2 border-blue-200 pl-3">
                  <p className="text-sm font-semibold text-slate-800">Version {entry.version} · {labelize(entry.changeType)}</p>
                  <p className="mt-1 text-sm text-slate-600">{entry.changeSummary}</p>
                  <p className="mt-1 text-xs text-slate-500">{formatDateTime(entry.createdAt)}{entry.changedByName ? ` · ${entry.changedByName}` : ''}</p>
                </div>
              ))}
              {!history?.length && <EmptyState>No policy versions recorded.</EmptyState>}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

const PaginationControls = ({ pagination, page, pageSize, onPageChange, onPageSizeChange, loading }) => {
  const total = Number(pagination?.total) || 0;
  const start = total ? (page * pageSize) + 1 : 0;
  const end = Math.min(total, (page + 1) * pageSize);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="flex flex-col gap-3 border-t border-slate-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm text-slate-500">
        Showing <span className="font-semibold text-slate-800">{start}-{end}</span> of <span className="font-semibold text-slate-800">{total}</span>
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-2 text-sm text-slate-500">
          Rows
          <select
            value={pageSize}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
            className="rounded-xl border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-700 outline-none ring-blue-500 focus:ring-2"
          >
            {[10, 20, 50].map((size) => <option key={size} value={size}>{size}</option>)}
          </select>
        </label>
        <button
          type="button"
          onClick={() => onPageChange(Math.max(0, page - 1))}
          disabled={loading || page <= 0}
          className="inline-flex items-center gap-1 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ChevronLeft size={15} /> Prev
        </button>
        <span className="rounded-xl bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-700">
          {page + 1} / {totalPages}
        </span>
        <button
          type="button"
          onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
          disabled={loading || page >= totalPages - 1}
          className="inline-flex items-center gap-1 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Next <ChevronRight size={15} />
        </button>
      </div>
    </div>
  );
};

const DocumentPreviewPanel = ({ preview, onClear }) => {
  const mimeType = preview.mimeType || preview.document?.mimeType || '';
  const canInline = preview.url && (mimeType.includes('pdf') || mimeType.startsWith('image/'));

  return (
    <aside className="rounded-xl border border-slate-100 bg-slate-950 p-3 text-white shadow-[0_18px_45px_rgba(15,23,42,0.16)] lg:sticky lg:top-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-200">Document preview</p>
          <p className="mt-1 truncate text-sm font-semibold">{preview.document?.originalFilename || 'Select a document'}</p>
        </div>
        {preview.url && (
          <button type="button" onClick={onClear} className="rounded-xl p-1 text-slate-300 hover:bg-white/10 hover:text-white">
            <X size={16} />
          </button>
        )}
      </div>
      <div className="flex min-h-[420px] items-center justify-center overflow-hidden rounded-xl bg-white">
        {preview.loading ? (
          <div className="w-full p-4">
            <SkeletonBlock className="h-[390px] rounded-xl" />
          </div>
        ) : preview.error ? (
          <div className="px-5 text-center text-sm leading-6 text-slate-600">{preview.error}</div>
        ) : !preview.url ? (
          <div className="px-5 text-center text-sm leading-6 text-slate-600">
            Choose Preview on a submitted document to inspect it here without leaving the review.
          </div>
        ) : canInline && mimeType.startsWith('image/') ? (
          <img src={preview.url} alt={preview.document?.originalFilename || 'KYC document preview'} className="max-h-[640px] w-full object-contain" />
        ) : canInline ? (
          <iframe title={preview.document?.originalFilename || 'KYC document preview'} src={preview.url} className="h-[640px] w-full border-0" />
        ) : (
          <div className="px-5 text-center text-sm leading-6 text-slate-600">
            This file type may not render inline. Use Download to inspect the original document.
          </div>
        )}
      </div>
      {preview.url && (
        <a href={preview.url} target="_blank" rel="noreferrer" className="mt-3 inline-flex w-full items-center justify-center rounded-xl border border-white/15 bg-white/10 px-3 py-2 text-sm font-semibold text-white hover:bg-white/15">
          Open preview in a new tab
        </a>
      )}
    </aside>
  );
};

const ReviewModal = ({
  open,
  details,
  latestDocuments,
  loading,
  error,
  pendingAction,
  documentPreview,
  onClose,
  onClearPreview,
  onAssign,
  onSuspend,
  onReactivate,
  onRejectEmployer,
  onApproveEmployer,
  onPreviewDocument,
  onDownloadDocument,
  onVerifyDocument,
  onRejectDocument
}) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 bg-slate-950/55 p-3 backdrop-blur-sm sm:p-6">
      <section className="mx-auto flex max-h-[94vh] w-full max-w-[1400px] flex-col overflow-hidden rounded-[24px] border border-slate-100 bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-700">Verification review</p>
            <h2 className="mt-1 text-xl font-semibold text-slate-950">
              {details?.employer?.companyName || 'Employer KYC'}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <X size={20} />
          </button>
        </div>

        <div className="overflow-y-auto p-5">
          {loading ? (
            <EmployerDetailSkeleton />
          ) : error ? (
            <EmptyState>{error?.message || 'Employer verification details could not be loaded.'}</EmptyState>
          ) : details ? (
            <div className="space-y-5">
              <section className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
                <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-xl font-semibold text-slate-950">{details.employer.companyName}</h3>
                      <Badge status={details.employer.verificationStatus} />
                      <Badge status={details.employer.accountStatus} />
                    </div>
                    <div className="mt-4 grid gap-2 text-sm text-slate-600 sm:grid-cols-2">
                      <span className="flex items-center gap-2"><Mail size={15} />{details.employer.email}</span>
                      <span className="flex items-center gap-2"><Building2 size={15} />{labelize(details.employer.accountType)}</span>
                      <span className="flex items-center gap-2"><MapPin size={15} />{details.employer.headquartersLocation || details.reviewSummary.regionName}</span>
                      <span className="flex items-center gap-2"><ShieldCheck size={15} />{details.reviewSummary.regionName} requirements</span>
                      <span className="flex items-center gap-2"><FileText size={15} />Policy version {details.reviewSummary.policyVersion}{details.reviewSummary.policySource === 'review_snapshot' ? ' · captured at submission' : ' · current policy'}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {!details.queue?.assignedTo && (
                      <button type="button" onClick={onAssign} disabled={Boolean(pendingAction)} className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                        Assign to me
                      </button>
                    )}
                    {details.employer.accountStatus === 'suspended' ? (
                      <button type="button" onClick={onReactivate} disabled={Boolean(pendingAction)} className="inline-flex items-center gap-2 rounded-xl border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50">
                        <RotateCcw size={15} /> Reactivate
                      </button>
                    ) : (
                      <button type="button" onClick={onSuspend} disabled={Boolean(pendingAction)} className="inline-flex items-center gap-2 rounded-xl border border-rose-300 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-50">
                        <Ban size={15} /> Suspend
                      </button>
                    )}
                  </div>
                </div>

                <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  {[
                    ['Required', details.reviewSummary.requiredCount],
                    ['Verified', details.reviewSummary.verifiedRequiredCount],
                    ['Pending', details.reviewSummary.pendingRequiredCount],
                    ['Rejected', details.reviewSummary.rejectedRequiredCount]
                  ].map(([label, value]) => (
                    <div key={label} className="rounded-xl border border-slate-100 bg-slate-50 p-3">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{label}</p>
                      <p className="mt-1 text-xl font-semibold text-slate-950">{value}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-5 flex flex-col gap-3 rounded-xl border border-slate-100 bg-slate-50 p-4 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {details.reviewSummary.allRequiredVerified ? 'All required documents are verified.' : 'Final approval remains locked.'}
                    </p>
                    <p className="mt-1 text-sm text-slate-600">
                      {details.reviewSummary.allRequiredVerified
                        ? 'Complete verification when the employer record is ready.'
                        : 'Verify each required document before approving the employer account.'}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={onRejectEmployer} disabled={Boolean(pendingAction) || details.employer.verificationStatus === 'verified'} className="rounded-xl border border-rose-300 bg-white px-3 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50">
                      Reject verification
                    </button>
                    <button type="button" onClick={onApproveEmployer} disabled={Boolean(pendingAction) || !details.reviewSummary.allRequiredVerified || details.employer.accountStatus === 'suspended' || details.employer.verificationStatus === 'verified'} className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50">
                      <CheckCircle2 size={15} /> Approve employer
                    </button>
                  </div>
                </div>
                {!details.reviewSummary.allRequiredVerified && (
                  <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-amber-800">Unresolved required documents</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {(details.reviewSummary.unresolvedRequiredDocuments || []).map((requirement) => (
                        <span key={requirement.slotKey || `${requirement.documentTypeId}-${requirement.subjectIndex}-${requirement.documentSide}`} className="inline-flex items-center gap-2 rounded-xl border border-amber-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700">
                          {requirement.slotLabel || requirement.displayName || labelize(requirement.documentType)}
                          <Badge status={requirement.status} />
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </section>

              <section className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_420px]">
                <div className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
                  <div className="mb-4 flex items-start gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 border border-blue-100/50"><FileText size={18} /></span>
                    <div>
                      <h3 className="text-base font-semibold text-slate-950">Submitted documents</h3>
                      <p className="mt-1 text-sm text-slate-500">Preview, verify, reject, or download the latest submission for each document type.</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {latestDocuments.length ? latestDocuments.map((document) => (
                      <article key={document.id} className={`rounded-xl border p-4 ${String(documentPreview.document?.id) === String(document.id) ? 'border-blue-300 bg-blue-50/50' : 'border-slate-100'}`}>
                        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <h4 className="text-sm font-semibold text-slate-950">{document.displayName || labelize(document.documentType)}</h4>
                              {document.isRequired && <span className="text-xs font-semibold text-rose-600">Required</span>}
                              <Badge status={document.verificationStatus} />
                            </div>
                            {(document.subjectType && document.subjectType !== 'company') || document.documentSide !== 'single' ? (
                              <p className="mt-1 text-xs font-medium text-slate-500">
                                {document.subjectName || `${labelize(document.subjectType)} ${document.subjectIndex || 1}`}
                                {document.documentSide && document.documentSide !== 'single' ? ` · ${labelize(document.documentSide)}` : ''}
                              </p>
                            ) : null}
                            <p className="mt-2 truncate text-sm text-slate-600">{document.originalFilename}</p>
                            <p className="mt-1 text-xs text-slate-500">{formatBytes(document.fileSizeBytes)} · Uploaded {formatDateTime(document.createdAt)}</p>
                            {document.rejectionReason && <p className="mt-2 text-sm font-medium text-rose-700">{document.rejectionReason}</p>}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <button type="button" onClick={() => onPreviewDocument(document)} disabled={Boolean(pendingAction) || documentPreview.loading} className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                              <Eye size={15} /> Preview
                            </button>
                            <button type="button" onClick={() => onDownloadDocument(document)} disabled={Boolean(pendingAction)} className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                              <Download size={15} /> Download
                            </button>
                            {details.employer.verificationStatus !== 'verified' && document.verificationStatus !== 'verified' && (
                              <button type="button" onClick={() => onVerifyDocument(document)} disabled={Boolean(pendingAction)} className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
                                <FileCheck2 size={15} /> Verify
                              </button>
                            )}
                            {details.employer.verificationStatus !== 'verified' && document.verificationStatus !== 'rejected' && (
                              <button type="button" onClick={() => onRejectDocument(document)} disabled={Boolean(pendingAction)} className="inline-flex items-center gap-2 rounded-xl border border-rose-300 bg-white px-3 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50">
                                <XCircle size={15} /> Reject
                              </button>
                            )}
                          </div>
                        </div>
                      </article>
                    )) : <EmptyState>No KYC documents have been supplied for this employer.</EmptyState>}
                  </div>
                </div>

                <DocumentPreviewPanel preview={documentPreview} onClear={onClearPreview} />
              </section>

              <section className="grid gap-5 lg:grid-cols-2">
                <div className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
                  <div className="mb-4 flex items-center gap-3">
                    <History size={18} className="text-blue-700" />
                    <h3 className="text-base font-semibold text-slate-950">Workflow history</h3>
                  </div>
                  <div className="space-y-3">
                    {(details.workflow || []).slice(0, 8).map((entry) => (
                      <div key={entry.id} className="border-l-2 border-blue-200 pl-3">
                        <p className="text-sm font-semibold text-slate-800">{labelize(entry.stepName)}</p>
                        <p className="mt-1 text-xs text-slate-500">{formatDateTime(entry.completedAt || entry.createdAt)}{entry.completedByName ? ` · ${entry.completedByName}` : ''}</p>
                        {entry.notes && <p className="mt-1 text-sm text-slate-600">{entry.notes}</p>}
                      </div>
                    ))}
                    {!details.workflow?.length && <EmptyState>No workflow events recorded.</EmptyState>}
                  </div>
                </div>
                <div className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
                  <div className="mb-4 flex items-center gap-3">
                    <ShieldCheck size={18} className="text-blue-700" />
                    <h3 className="text-base font-semibold text-slate-950">Audit trail</h3>
                  </div>
                  <div className="space-y-3">
                    {(details.auditTrail || []).slice(0, 8).map((entry) => (
                      <div key={entry.id} className="border-l-2 border-slate-100 pl-3">
                        <p className="text-sm font-semibold text-slate-800">{labelize(entry.action)}</p>
                        <p className="mt-1 text-xs text-slate-500">{formatDateTime(entry.createdAt)}{entry.actorName ? ` · ${entry.actorName}` : ''}</p>
                      </div>
                    ))}
                    {!details.auditTrail?.length && <EmptyState>No KYC audit events recorded.</EmptyState>}
                  </div>
                </div>
              </section>
            </div>
          ) : (
            <EmptyState>Select an employer verification record to begin review.</EmptyState>
          )}
        </div>
      </section>
    </div>
  );
};

function KycVerificationPage() {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const canManagePolicies = hasPermission(user, 'config.manage');
  const [section, setSection] = useState('reviews');
  const [filters, setFilters] = useState({ status: 'open', search: '' });
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [selectedEmployerId, setSelectedEmployerId] = useState(null);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [selectedRegionId, setSelectedRegionId] = useState(null);
  const [pendingAction, setPendingAction] = useState('');
  const [modal, setModal] = useState(null);
  const [requirementModal, setRequirementModal] = useState(null);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [documentPreview, setDocumentPreview] = useState({
    loading: false,
    document: null,
    url: '',
    mimeType: '',
    error: ''
  });

  const queueQuery = useQuery(
    ['admin-kyc-queue', filters, page, pageSize],
    () => adminAPI.getVerificationQueue({ ...filters, limit: pageSize, offset: page * pageSize }),
    { keepPreviousData: true, refetchInterval: 60000 }
  );
  const detailQuery = useQuery(
    ['admin-kyc-detail', selectedEmployerId],
    () => adminAPI.getEmployerVerificationDetails(selectedEmployerId),
    { enabled: reviewModalOpen && Boolean(selectedEmployerId) }
  );
  const policiesQuery = useQuery(
    'admin-kyc-regional-policies',
    adminAPI.getRegionalKycPolicies,
    { enabled: section === 'policies' }
  );
  const policies = useMemo(() => policiesQuery.data?.policies || [], [policiesQuery.data]);
  const policyHistoryQuery = useQuery(
    ['admin-kyc-policy-history', selectedRegionId],
    () => adminAPI.getRegionalKycPolicyHistory(selectedRegionId),
    { enabled: section === 'policies' && Boolean(selectedRegionId) }
  );
  const queue = useMemo(() => queueQuery.data?.queue || [], [queueQuery.data]);
  const stats = queueQuery.data?.stats || {};
  const details = detailQuery.data;

  const pagination = queueQuery.data?.pagination || {};

  useEffect(() => {
    if (!policies.length) {
      setSelectedRegionId(null);
      return;
    }
    if (!selectedRegionId || !policies.some((policy) => String(policy.id) === String(selectedRegionId))) {
      setSelectedRegionId(policies[0].id);
    }
  }, [policies, selectedRegionId]);

  const latestDocuments = useMemo(
    () => (details?.documents || []).filter((document) => document.isLatest),
    [details]
  );

  useEffect(() => () => {
    if (documentPreview.url) URL.revokeObjectURL(documentPreview.url);
  }, [documentPreview.url]);

  useEffect(() => {
    const total = Number(pagination.total) || 0;
    const maxPage = Math.max(0, Math.ceil(total / pageSize) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [pagination.total, page, pageSize]);

  const clearDocumentPreview = () => {
    setDocumentPreview({
      loading: false,
      document: null,
      url: '',
      mimeType: '',
      error: ''
    });
  };

  const openReview = (entry) => {
    clearDocumentPreview();
    setSelectedEmployerId(entry.employerId);
    setReviewModalOpen(true);
  };

  const closeReview = () => {
    setReviewModalOpen(false);
    setSelectedEmployerId(null);
    clearDocumentPreview();
  };

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries('admin-kyc-queue'),
      queryClient.invalidateQueries('admin-kyc-detail'),
      queryClient.invalidateQueries('admin-kyc-regional-policies'),
      queryClient.invalidateQueries('admin-kyc-policy-history')
    ]);
  };

  const handleRequirementSave = async (form) => {
    if (!requirementModal) return;
    const payload = {
      displayName: form.displayName.trim(),
      documentDescription: form.documentDescription.trim(),
      isRequired: form.isRequired,
      isActive: form.isActive,
      documentCategory: form.documentCategory,
      subjectType: form.subjectType,
      allowMultiple: form.subjectType === 'company' ? false : form.allowMultiple,
      minimumInstances: form.subjectType === 'company' ? 1 : Number(form.minimumInstances) || 1,
      maximumInstances: form.subjectType === 'company' ? 1 : Number(form.maximumInstances) || Number(form.minimumInstances) || 1,
      requiredSides: form.requiredSides,
      instanceLabel: form.instanceLabel.trim(),
      identifierLabel: form.identifierLabel.trim(),
      validationRule: form.validationRule,
      validationPattern: form.validationRule === 'regex' ? form.validationPattern.trim() : '',
      validationHelpText: form.validationHelpText?.trim?.() || '',
      uploadInstructions: form.uploadInstructions.trim(),
      changeSummary: form.changeSummary.trim()
    };
    const editing = requirementModal.type === 'editRequirement';
    const saved = await runAction(
      editing ? `update-requirement-${requirementModal.requirement.id}` : `create-requirement-${requirementModal.policy.id}`,
      () => editing
        ? adminAPI.updateRegionalKycRequirement(requirementModal.requirement.id, payload)
        : adminAPI.createRegionalKycRequirement(requirementModal.policy.id, {
          ...payload,
          documentType: form.documentType.trim()
        }),
      editing ? 'Regional KYC requirement updated.' : 'Regional KYC requirement added.'
    );
    if (saved) setRequirementModal(null);
  };

  const runAction = async (key, action, successMessage) => {
    try {
      setPendingAction(key);
      await action();
      toast.success(successMessage);
      setModal(null);
      setReason('');
      setNotes('');
      await refresh();
      return true;
    } catch (error) {
      toast.error(error?.message || 'The KYC action could not be completed.');
      return false;
    } finally {
      setPendingAction('');
    }
  };

  const handleModalConfirm = () => {
    if (!modal || !selectedEmployerId) return;
    if (modal.type === 'rejectDocument') {
      runAction(
        `reject-document-${modal.document.id}`,
        () => adminAPI.updateVerificationDocument(modal.document.id, {
          status: 'rejected',
          rejectionReason: reason.trim(),
          notes: notes.trim()
        }),
        'Document rejected and employer notification queued.'
      );
    } else if (modal.type === 'rejectEmployer') {
      runAction(
        'reject-employer',
        () => adminAPI.updateVerificationStatus(selectedEmployerId, {
          status: 'rejected',
          rejectionReason: reason.trim(),
          notes: notes.trim()
        }),
        'Employer verification rejected and notification queued.'
      );
    } else if (modal.type === 'suspendEmployer') {
      runAction(
        'suspend-employer',
        () => adminAPI.updateEmployerAccountStatus(selectedEmployerId, {
          status: 'suspended',
          reason: reason.trim()
        }),
        'Employer account suspended and notification queued.'
      );
    }
  };

  const handleBlob = async (document, preview) => {
    try {
      setPendingAction(`${preview ? 'preview' : 'download'}-${document.id}`);
      const blob = preview
        ? await adminAPI.previewVerificationDocument(document.id)
        : await adminAPI.downloadVerificationDocument(document.id);
      openBlob(blob, document.originalFilename, preview);
    } catch (error) {
      toast.error(error?.message || 'The document could not be opened.');
    } finally {
      setPendingAction('');
    }
  };

  const handlePreviewDocument = async (document) => {
    setDocumentPreview({
      loading: true,
      document,
      url: '',
      mimeType: document.mimeType || '',
      error: ''
    });

    try {
      const blob = await adminAPI.previewVerificationDocument(document.id);
      const url = URL.createObjectURL(blob);
      setDocumentPreview({
        loading: false,
        document,
        url,
        mimeType: blob.type || document.mimeType || '',
        error: ''
      });
    } catch (error) {
      setDocumentPreview({
        loading: false,
        document,
        url: '',
        mimeType: document.mimeType || '',
        error: error?.message || 'The document preview could not be loaded.'
      });
      toast.error(error?.message || 'The document preview could not be loaded.');
    }
  };

  const handlePageSizeChange = (nextPageSize) => {
    setPageSize(nextPageSize);
    setPage(0);
  };

  return (
    <div className="min-h-full bg-[#f8f9fa] p-4 sm:p-6 lg:p-8 font-sans">
      <ReasonModal
        modal={modal}
        reason={reason}
        notes={notes}
        loading={Boolean(pendingAction)}
        onReasonChange={setReason}
        onNotesChange={setNotes}
        onClose={() => { setModal(null); setReason(''); setNotes(''); }}
        onConfirm={handleModalConfirm}
      />
      <RequirementModal
        modal={requirementModal}
        loading={Boolean(pendingAction)}
        onClose={() => setRequirementModal(null)}
        onConfirm={handleRequirementSave}
      />
      <ReviewModal
        open={reviewModalOpen}
        details={details}
        latestDocuments={latestDocuments}
        loading={detailQuery.isLoading}
        error={detailQuery.error}
        pendingAction={pendingAction}
        documentPreview={documentPreview}
        onClose={closeReview}
        onClearPreview={clearDocumentPreview}
        onAssign={() => runAction('assign', () => adminAPI.assignVerification(selectedEmployerId), 'Review assigned to you.')}
        onSuspend={() => setModal({ type: 'suspendEmployer' })}
        onReactivate={() => runAction('reactivate-employer', () => adminAPI.updateEmployerAccountStatus(selectedEmployerId, { status: 'active' }), 'Employer account reactivated and notification queued.')}
        onRejectEmployer={() => setModal({ type: 'rejectEmployer' })}
        onApproveEmployer={() => runAction('approve-employer', () => adminAPI.updateVerificationStatus(selectedEmployerId, { status: 'verified' }), 'Employer verified and account-ready email queued.')}
        onPreviewDocument={handlePreviewDocument}
        onDownloadDocument={(document) => handleBlob(document, false)}
        onVerifyDocument={(document) => runAction(`verify-document-${document.id}`, () => adminAPI.updateVerificationDocument(document.id, { status: 'verified' }), 'Document verified.')}
        onRejectDocument={(document) => setModal({ type: 'rejectDocument', document })}
      />

      
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">KYC Verification</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-500">
            {section === 'reviews'
              ? 'Review employer documents individually, resolve partial rejections, complete verification, and control organization access.'
              : 'Manage the regional document policies used for new employer submissions while preserving immutable in-flight review snapshots.'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 rounded-xl bg-slate-100 p-1 border border-slate-200">
          <button type="button" onClick={() => setSection('reviews')} className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition ${section === 'reviews' ? 'bg-white text-slate-900 shadow-sm border border-slate-200' : 'text-slate-500 hover:text-slate-900'}`}>
            <ClipboardCheck size={16} /> Reviews
          </button>
          <button type="button" onClick={() => setSection('policies')} className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition ${section === 'policies' ? 'bg-white text-slate-900 shadow-sm border border-slate-200' : 'text-slate-500 hover:text-slate-900'}`}>
            <Settings2 size={16} /> Regional Policies
          </button>
        </div>
      </div>


      {section === 'reviews' ? (
        <>
      {queueQuery.isLoading ? (
        <SummaryCardsSkeleton />
      ) : (
        <section className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <SummaryCard icon={ClipboardCheck} label="Open queue" value={stats.open} />
          <SummaryCard icon={Clock3} label="Pending" value={stats.pending} tone="amber" />
          <SummaryCard icon={UserCheck} label="Assigned" value={stats.assigned} tone="violet" />
          <SummaryCard icon={CheckCircle2} label="Completed" value={stats.completed} tone="emerald" />
        </section>
      )}

      
      <section className="rounded-[24px] border border-slate-100 bg-white shadow-sm overflow-hidden flex flex-col">
        <div className="border-b border-slate-100 p-5 sm:p-6 bg-white flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <form
            className="flex flex-col gap-3 sm:flex-row flex-1"
            onSubmit={(event) => {
              event.preventDefault();
              setFilters((current) => ({ ...current, search: searchInput.trim() }));
              setPage(0);
            }}
          >
            <label className="relative flex-1 block">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search company, contact, or email"
                className="w-full h-10 rounded-xl border border-slate-200 bg-slate-50 pl-11 pr-4 text-sm text-slate-700 outline-none transition focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
            </label>
            <div className="flex gap-3 flex-col sm:flex-row shrink-0">
              <select
                value={filters.status}
                onChange={(event) => {
                  setFilters((current) => ({ ...current, status: event.target.value }));
                  setPage(0);
                }}
                className="h-10 rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm text-slate-700 outline-none transition focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 cursor-pointer min-w-[160px]"
              >
                {queueStatuses.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
              </select>
              <button type="submit" className="inline-flex h-10 items-center justify-center rounded-xl bg-slate-900 px-6 text-sm font-semibold text-white transition hover:bg-blue-600 shadow-sm shrink-0">
                Search
              </button>
            </div>
          </form>
          {queueQuery.isFetching && !queueQuery.isLoading && (
            <span className="inline-flex items-center gap-2 rounded-xl bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 shrink-0">
              <Loader2 size={14} className="animate-spin" /> Updating
            </span>
          )}
        </div>

        {queueQuery.isLoading ? (
          <QueueListSkeleton />
        ) : queueQuery.isError ? (
          <div className="p-4"><EmptyState>{queueQuery.error?.message || 'The verification queue could not be loaded.'}</EmptyState></div>
        ) : queue.length ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead>
                <tr className="bg-slate-50/50">
                  <th scope="col" className="px-6 py-4 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Employer</th>
                  <th scope="col" className="px-6 py-4 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Queue</th>
                  <th scope="col" className="px-6 py-4 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Documents</th>
                  <th scope="col" className="px-6 py-4 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Age</th>
                  <th scope="col" className="px-6 py-4 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Reviewer</th>
                  <th scope="col" className="px-6 py-4 text-right text-[10px] font-bold text-slate-400 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {queue.map((entry) => {
                  const uploaded = Number(entry.documentsUploaded) || 0;
                  const verified = Number(entry.documentsVerified) || 0;
                  const progress = uploaded ? Math.min(100, Math.round((verified / uploaded) * 100)) : 0;
                  return (
                    <tr key={entry.queueId} className="align-top hover:bg-slate-50/80 transition-colors group">
                      <td className="max-w-sm px-6 py-4">
                        <div className="flex items-center gap-4 min-w-[240px]">
                          <div className="w-10 h-10 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 font-bold overflow-hidden shrink-0 border border-blue-100/50 shadow-sm">
                            {initialsFor(entry.companyName)}
                          </div>
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="truncate text-sm font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">{entry.companyName}</p>
                              {entry.accountStatus === 'suspended' && <Badge status="suspended">Suspended</Badge>}
                            </div>
                            <p className="mt-1 truncate text-xs text-slate-500">{entry.email}</p>
                            <p className="mt-0.5 truncate text-[11px] text-slate-400">{entry.contactName || 'No contact name'}{entry.headquartersLocation ? ` · ${entry.headquartersLocation}` : ''}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-2">
                          <Badge status={entry.queueStatus} />
                          <p className="text-xs text-slate-500">Priority {entry.priority || 1}</p>
                        </div>
                      </td>
                      <td className="min-w-[170px] px-6 py-4">
                        <div className="flex items-center justify-between gap-3 text-sm">
                          <span className="font-semibold text-slate-800">{verified}/{uploaded}</span>
                          <span className="text-xs text-slate-500">{entry.documentsRejected || 0} rejected</span>
                        </div>
                        <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
                          <div className="h-full rounded-full bg-blue-600" style={{ width: `${progress}%` }} />
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm font-semibold text-slate-800">{Number(entry.daysPending || 0).toFixed(1)} days</p>
                        <p className="mt-1 text-xs text-slate-500">Created {formatDateTime(entry.createdAt)}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm font-medium text-slate-700">{entry.assignedReviewer || 'Unassigned'}</p>
                        {entry.assignedAt && <p className="mt-1 text-xs text-slate-500">{formatDateTime(entry.assignedAt)}</p>}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button type="button" onClick={() => openReview(entry)} className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-50 text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors shadow-sm border border-slate-100/50 inline-flex ml-auto" title="Review KYC">
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-4"><EmptyState>No employer verifications match the selected filters.</EmptyState></div>
        )}

        <PaginationControls
          pagination={pagination}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
          loading={queueQuery.isFetching}
        />
      </section>
        </>
      ) : (
        <RegionalPolicyWorkspace
          policies={policies}
          loading={policiesQuery.isLoading}
          error={policiesQuery.error}
          canManage={canManagePolicies}
          selectedRegionId={selectedRegionId}
          onSelectRegion={setSelectedRegionId}
          history={policyHistoryQuery.data?.history || []}
          historyLoading={policyHistoryQuery.isLoading}
          onAdd={(policy) => setRequirementModal({ type: 'addRequirement', policy })}
          onEdit={(policy, requirement) => setRequirementModal({ type: 'editRequirement', policy, requirement })}
        />
      )}
    </div>
  );
}

export default KycVerificationPage;
